import * as React from 'react';
import { VariableSizeList } from 'react-window';

import { ListBoxRow } from '@/shared/components/ui/form/components/searchable-select/components/list-box/components/list-box-row/list-box-row';
import {
  ListBoxWrapper,
  ListBoxWrapperContext,
} from '@/shared/components/ui/form/components/searchable-select/components/list-box/components/list-box-wrapper/list-box-wrapper';
import { useItemData } from '@/shared/components/ui/form/components/searchable-select/components/list-box/hooks/use-item-data/use-item-data';
import { useResetCache } from '@/shared/components/ui/form/components/searchable-select/components/list-box/hooks/use-reset-cache/use-reset-cache';
import {
  GROUP_HEADER_SIZE,
  LISTBOX_PADDING,
  MAX_VISIBLE_ROWS,
  ROW_SIZE,
} from '@/shared/components/ui/form/components/searchable-select/components/list-box/list-box.constants';
import { TListBoxProps } from '@/shared/components/ui/form/components/searchable-select/components/list-box/list-box.types';
import { isGroupHeader } from '@/shared/components/ui/form/components/searchable-select/components/list-box/list-box.utils';

export const ListboxComponent: React.FC<TListBoxProps> = ({
  ref,
  ...props
}) => {
  const { children, ...other } = props;
  const itemData: React.ReactElement[] = useItemData({
    children: children as React.ReactElement[],
  });

  const itemCount = itemData.length;

  const getItemSize = (index: number) =>
    isGroupHeader(itemData[index]) ? GROUP_HEADER_SIZE : ROW_SIZE;

  const getHeight = () => {
    return (
      itemData
        .slice(0, MAX_VISIBLE_ROWS)
        .reduce((height, _, index) => height + getItemSize(index), 0) +
      2 * LISTBOX_PADDING
    );
  };

  // Row sizes are cached by index, so they must be dropped whenever a group
  // header moves — not only when the number of options changes.
  const layout = itemData
    .map((item) => (isGroupHeader(item) ? 'g' : 'o'))
    .join('');

  const gridRef = useResetCache(layout);

  return (
    <div ref={ref}>
      <ListBoxWrapperContext value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight()}
          width="100%"
          ref={gridRef}
          outerElementType={ListBoxWrapper}
          innerElementType="ul"
          itemSize={getItemSize}
          estimatedItemSize={ROW_SIZE}
          overscanCount={5}
          itemCount={itemCount}
        >
          {ListBoxRow}
        </VariableSizeList>
      </ListBoxWrapperContext>
    </div>
  );
};
